/* eslint-disable max-lines */
import { isEmpty } from 'lodash-es';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map, take, tap, switchMap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener,
  Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation
} from '@angular/core';
import * as Avo from '@avo/Avo';

import { isNullOrUndefined } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { ToastrTypes } from '@app/entities/toastr';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import { environment as ENV } from '@environment';
import { SearchType } from '@search-bar/entities/search-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TimeField } from '@shared/base-search-bar/entities/time.entities';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { RappiTravelModules } from '@ux/entities/modules';
import { blockSearchByDates, canSearch, isNationalSearch } from '@ux/mappers/date.mapper';
import { AutoSearchAbstract } from '@autos/auto-search/abstract/auto-search.abstract';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { AvoService } from '@avo/services/avo.service';
import { EventProperties } from '@avo/interface/trigger-props';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';

@Component({
  selector: 'app-auto-search',
  templateUrl: './auto-search.component.html',
  styleUrls: ['./auto-search.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class AutoSearchComponent extends AutoSearchAbstract implements OnInit, OnDestroy, OnChanges {
  @ViewChild('searchContent') public searchContent: ElementRef;

  public pickUpDate: Date;
  public dropOffDate: Date;
  public pickUpTime: string = '';
  public dropOffTime: string = '';
  public showCalendarInput: boolean = false;
  public activeCalendarType: CalendarField = CalendarField.departure;
  public pickUp: PlaceType = PlaceType.DEPARTURE;
  public dropOff: PlaceType = PlaceType.ARRIVAL;
  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);
  public isSubmitted: boolean = false;

  private subscriptions: Subscription[] = [];
  private placeDropOffSelectedItem: SelectAutocompletePrediction;
  private placePickUpSelectedItem: SelectAutocompletePrediction;
  
  protected AvoDispatch = new AvoDispatchOrchestrator();

  @Input() searchType: SearchType = SearchType.HOME_AUTOS;
  @Input() source: EVENT_SOURCE | string;
  @Input() showSearch: boolean = false;
  @Input() loadSearchData: boolean = false;
  @Input() isResults: boolean = false;
  @Input() showAutoDetail: boolean;
  @Input() isMobile: boolean;
  @Input() autoDetail: boolean = false;
  @Input() loadDefaultplaces: boolean;

  @Output() showPickUp: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showDropOff: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() redirectToSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();

  @HostListener('document:click', ['$event']) clickout(event: Event): void {
    if (this.isBrowser) this.eventPath.next(event.composedPath());
  }

  constructor(
    private readonly amplitude: PropertiesAmplitudeService,
    private readonly appFacade: AppFacade,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslocoService,
    private readonly triggerAmplitudeService: TriggerAmplitudeService,
    private readonly avoService: AvoService,
    protected readonly baseSearchbarfacade: BaseSearchBarFacade,
    protected readonly viewportScroller: ViewportScroller,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(baseSearchbarfacade, viewportScroller);
    this.loadDefaultplaces = true;
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (!this.isResults && !this.autoDetail && this.loadDefaultplaces) {
        this.baseSearchbarfacade.loadSearchData(SearchBy.AUTOS);
      }
      this.subscriptions.push(
        this.baseSearchbarfacade.searchData$.pipe(filter(data => !!data))
          .pipe(map(searchData => ({ searchData }))).subscribe(({ searchData }) => {
            this.placeDropOffSelectedItem = searchData.dropOffPlace;
            this.placePickUpSelectedItem = searchData.pickUpPlace;
            this.pickUpDate = searchData.departureDate;
            if (searchData.pickUpTime) {
              this.pickUpTime = searchData.pickUpTime;
            }
            this.dropOffDate = searchData.arrivalDate;
            if (searchData.dropOffTime) {
              this.dropOffTime = searchData.dropOffTime;
            }
            this.cdr.detectChanges(); this.cdr.markForCheck();
          }));
      if (this.searchType === SearchType.HOME_AUTOS) this.registerViewOrSelectAmplitudeEvent(this.source);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showSearch && changes.showSearch.currentValue) {
      const source = (this.source === EVENT_SOURCE.RESULTS && this.showAutoDetail ? EVENT_SOURCE.DETAIL : this.source);
      this.registerViewOrSelectAmplitudeEvent(source);
    }
  }

  ngOnDestroy(): void {
    this.cdr.detach(); this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isAutoDetail(): boolean {
    return this.searchType === SearchType.AUTOS_DETAIL;
  }
  get searchButtonLabel(): string {
    return this.isAutoDetail ? 'BUTTONS.EDIT' : 'BUTTONS.SEARCH';
  }
  get autoSearchHome(): boolean {
    return this.searchType === SearchType.HOME_AUTOS;
  }

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }

  get timeField(): typeof TimeField {
    return TimeField;
  }

  get isPickUpDateEmpty(): boolean {
    return isNullOrUndefined(this.pickUpDate) && this.isSubmitted;
  }

  get isDropOffDateEmpty(): boolean {
    return isNullOrUndefined(this.dropOffDate) && this.isSubmitted;
  }

  get isPickUpTimeEmpty(): boolean {
    return isNullOrUndefined(this.pickUpTime) && this.isSubmitted;
  }

  get isDropOffTimeEmpty(): boolean {
    return isNullOrUndefined(this.dropOffTime) && this.isSubmitted;
  }

  get searchValid(): boolean {
    const validatePlace = this.isAutoDetail ? true : (!this.isPlacePickUpSelectedEmpty && !this.isPlaceDropOffSelectedEmpty);
    return validatePlace && !isNullOrUndefined(this.pickUpDate) && !isNullOrUndefined(this.dropOffDate);
  }

  get isPlacePickUpSelectedEmpty(): boolean {
    return (isNullOrUndefined(this.placePickUpSelectedItem) ||
      isEmpty(this.placePickUpSelectedItem) || !(this.placePickUpSelectedItem.code ||
        this.placePickUpSelectedItem.description)) && this.isSubmitted;
  }

  get isPlaceDropOffSelectedEmpty(): boolean {
    return (isNullOrUndefined(this.placeDropOffSelectedItem) ||
      isEmpty(this.placeDropOffSelectedItem) || !(this.placeDropOffSelectedItem.code ||
        this.placeDropOffSelectedItem.description)) && this.isSubmitted;
  }

  public selectPlace(place: SelectAutocompletePrediction, type: PlaceType): void {
    this.baseSearchbarfacade.selectAutoPlaces(place);
    if (type === PlaceType.ARRIVAL) {
      this.placeDropOffSelectedItem = place;
    } else {
      this.placePickUpSelectedItem = place;
    }
    this.baseSearchbarfacade.searchData$.pipe(take(1)).subscribe(
      search => this.handleSearchDataSaving(search)
    ).unsubscribe();
  }

  public showMobileCalendar(type: CalendarField): void {
    this.showMobileCalendarEvent.emit(type);
  }

  public navigateToSearch(): void {
    this.isSubmitted = true;
    this.cdr.detectChanges(); this.cdr.markForCheck();
    if (this.searchValid) {
      this.baseSearchbarfacade.searchData$.pipe(
        take(1),
        tap(() => this.redirectToSearch.emit()),
        switchMap(data => this.amplitude.searchProperties$(SearchBy.AUTOS).pipe(
          take(1), map(searchProps => ({
            _source: this.showAutoDetail ? EVENT_SOURCE.DETAIL : this.source, searchProps
          })),
          switchMap(({ searchProps, _source }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
            AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: _source }, searchProps
          )),
          tap(trigger => {
            if (!canSearch(RappiTravelModules.autos) &&
              blockSearchByDates(data.destinationPlace, data.departureDate, ENV.validate_search.autos)) {
              this.isSubmitted = false;
              this.cdr.detectChanges(); this.cdr.markForCheck();
              const messageType = isNationalSearch(data.destinationPlace) ? 'AUTO_NATIONAL' : 'AUTO_INTERNATIONAL';
              this.appFacade.fetchToastrMessage({
                type: ToastrTypes.ERROR,
                message: this.translateService.translate(`BLOCK_SEARCH_MESSAGE.${messageType}`)
              });
            } else {
              this.baseSearchbarfacade.saveSearchData({ ...data }, SearchBy.AUTOS);
              Avo.selectTravelSearch({
                ...this.avoService.avoBaseProperties(trigger),
                source: this.source as Avo.SourceValueType,
              });
            }
          })
        )),
      ).subscribe().unsubscribe();
    }
  }

  private handleSearchDataSaving(search: SearchData): void {
    if (this.searchType === SearchType.HOME_AUTOS) {
      this.baseSearchbarfacade.saveSearchData(search, SearchBy.AUTOS);
    }
  }

  private registerViewOrSelectAmplitudeEvent(source: EVENT_SOURCE | string, isClickAction: boolean = false): void {
    if (isClickAction) {
      this.amplitude.searchProperties$(SearchBy.AUTOS).pipe(
        take(1), map(searchProps => ({
          _source: this.showAutoDetail ? EVENT_SOURCE.DETAIL : source, searchProps
        })),
        switchMap(({ searchProps, _source }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
          AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: _source }, searchProps
        )),
        tap(trigger =>
          this.AvoDispatch.selectTravelSearch({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.source as Avo.SourceValueType,
          }))
      ).subscribe().unsubscribe();
    } else {
      this.triggerAmplitudeService.trackAmplitudeEventProps$(
        AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN, { SOURCE: source, TRAVEL_PRODUCT_TYPE: SearchBy.AUTOS }
      ).pipe(
        tap(trigger =>
          this.AvoDispatch.viewTravelSearchScreen({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.source as Avo.SourceValueType,
            travelProductType: SearchBy.AUTOS
          })
        )
      ).subscribe().unsubscribe();
    }
  }

  public clearAutocompletePlaces(): void {
    this.baseSearchbarfacade.resetAutocomplete();
  }

  public updateTime(time: string, type: TimeField): void {
    if (type === TimeField.arrival) {
      this.dropOffTime = time;
    } else {
      this.pickUpTime = time;
    }
  }

  public emitShowPickUp(value: boolean): void {
    this.showPickUp.emit(value);
  }

  public emitShowDropOff(value: boolean): void {
    this.showDropOff.emit(value);
  }

  public selectCalendarDates(dates: Date[]): void {
    this.pickUpDate = dates[0];
    this.dropOffDate = dates[1];
    this.baseSearchbarfacade.selectCalendarDates([this.pickUpDate, this.dropOffDate]);
    this.baseSearchbarfacade.selectCalendarTimes([this.pickUpTime, this.dropOffTime]);
    this.cdr.detectChanges(); this.cdr.markForCheck();
  }

  public selectTimes(times: string[]): void {
    this.pickUpTime = times[0];
    this.dropOffTime = times[1];
    this.baseSearchbarfacade.selectCalendarDates([this.pickUpDate, this.dropOffDate]);
    this.baseSearchbarfacade.selectCalendarTimes(times);
    this.cdr.detectChanges(); this.cdr.markForCheck();
  }

  public locationNotFoundEvent(props: EventProperties): void {
    this.amplitudeEvent({ type: AVO_EVENTS.TRAVEL_LOCATION_NOT_FOUND, props });
  }

  public popularSelectedEvent(props: EventProperties): void {
    this.amplitudeEvent({ type: AMPLITUDE_EVENTS.SELECT_TRAVEL_POPULAR_DESTINATIONS, props });
  }

  private amplitudeEvent(params: AmplitudeEventParams): void {
    this.avoService.getEventTriggerProps$(params.type, params.props)
      .pipe(
        tap(trigger => {
          if (trigger.EVENT_NAME === AVO_EVENTS.TRAVEL_LOCATION_NOT_FOUND) {
            this.AvoDispatch.travelLocationNotFound({
              ...this.avoService.avoBaseProperties(trigger),
              searchQuery: trigger?.EVENT_PROPS?.SEARCH_QUERY as string,
              controlId: trigger?.EVENT_PROPS?.CONTROL_ID as string,
              travelProductType: trigger?.EVENT_PROPS?.TRAVEL_PRODUCT_TYPE as string,
            })
          } else {
            triggerAmplitudeEventApp(trigger);
          }
        })
      ).subscribe().unsubscribe();
  }
}
